// Onboarding tour steps, numbered 1..10 to match onboarding_tours.last_step.
// Each step belongs to the page it highlights; the controller only builds the
// steps for the current page and hands off to the next page between steps 2 and 3.
import { MOBILE_BREAKPOINT } from "../ruby_ui/sidebar_controller.js"

export const LAST_STEP = 10

// The server is the only record of progress, so the library's own localStorage
// memory stays off, and the page stays inert while a step is shown.
export const CLIENT_OPTIONS = Object.freeze({
  rememberStep: false,
  completeOnFinish: false,
  exitOnClickOutside: false,
  activeStepInteraction: false,
  showStepDots: false,
  showStepProgress: false,
  debug: false,
  // Total padding around a highlight (half per side). The default 30 spilled 15px
  // into the sidebar item above and past the sidebar's edges; 8 hugs the target.
  targetPadding: 8,
})

const SKIP_BUTTON ='<button type="button" class="tg-skip" data-tour-skip>Skip tour</button>'

const STEPS = [
  { number: 1, page: "dashboard", title: "Welcome to SHQ Connect",
    content: "This short tour shows the order to set things up: connect a store, map your products, then watch orders arrive." },
  { number: 2, page: "dashboard", title: "Start with your stores", sidebar: "stores", label: "Stores",
    content: "Your sales channels live under Stores. Click Next to go there." },
  { number: 3, page: "stores", title: "Connected stores", target: "h1",
    content: "This page lists every store you connect and whether it is healthy." },
  { number: 4, page: "stores", title: "Connect a store", target: '[data-tour="connect-store"]',
    content: "Use Connect store to link a sales channel to SHQ Connect." },
  { number: 5, page: "stores", title: "Choose a channel", target: '[data-tour="channel-picker"]', picker: true,
    content: "Etsy is available today. Shopify and WooCommerce are coming soon." },
  { number: 6, page: "stores", title: "How connecting works",
    media: {
      key: "connect", figure: "stores-connected", crops: [ { name: "stores", mark: true } ],
      alt: "Two Etsy stores in the store list, each marked Connected",
    },
    content: "Sign in to Etsy and approve SHQ Connect. Your store then shows up here marked Connected, and its listings start syncing." },
  { number: 7, page: "stores", title: "Map your products", sidebar: "mapping", label: "Mapping",
    content: "Mapping links each product in your store to its StationeryHQ product." },
  { number: 8, page: "stores", title: "Choosing products to map",
    media: {
      key: "mapping", figure: "product-mapping",
      crops: [ { name: "source", mark: true }, { name: "destination" } ],
      alt: "Step 1, Source: Frecklebox Book selected from your store. Step 2, Destination: a StationeryHQ product to map it to",
    },
    content: "Pick one of your store's products, then the StationeryHQ product it becomes when it's ordered." },
  { number: 9, page: "stores", title: "Track your orders", sidebar: "orders", label: "Order",
    content: "Orders for mapped products show up under Order." },
  { number: 10, page: "stores", title: "Following your orders",
    media: {
      key: "orders", figure: "orders-page", splice: true,
      crops: [ { name: "order-numbers" }, { name: "order-statuses", mark: true } ],
      alt: "Two orders in the orders list, each with status Forwarded and payment status Paid",
    },
    content: "Paid orders arrive here by themselves. Forwarded means the order went on to StationeryHQ to be fulfilled." },
]

export function stepsForPage(page) {
  return STEPS.filter((step) => step.page === page)
}

export function pageForStep(number) {
  return STEPS.find((step) => step.number === number)?.page
}

// Shown on the dashboard to a seller who left a first run on another page: Next
// takes them back to their saved step instead of the dashboard redirecting on load.
export function continueStep(number) {
  return {
    number, page: "dashboard", resume: true, title: "Continue the tour",
    content: `You stopped at step ${number} of ${LAST_STEP}. Click Next to pick up where you left off.`,
  }
}

// Sidebar items are hidden inside the drawer below the breakpoint, so those steps
// point at the header menu button instead.
export function targetSelector(step, width) {
  if (!step.sidebar) return step.target ?? null
  return width < MOBILE_BREAKPOINT ? '[data-tour="menu"]' : `[data-tour="${step.sidebar}"]`
}

// mediaUrls maps each step's media key to its fingerprinted asset URL.
export function stepContent(step, width, mediaUrls) {
  let body = step.content
  // The page is inert while a step shows, so this names where the item lives rather
  // than asking for a tap.
  if (step.sidebar && width < MOBILE_BREAKPOINT) body = `On a phone, ${step.label} is in this menu. ${body}`
  if (step.media) body += mediaFigure(step.media, mediaUrls[step.media.key])
  return `<p class="tg-step-count">Step ${step.number} of ${LAST_STEP}</p>${body}${SKIP_BUTTON}`
}

// A named figure shows one or more crops of its screenshot (each sized in CSS) and
// rings the thing to look for. Several crops are joined by an arrow ("this becomes
// that"), or spliced into one table with a seam where columns were cut.
// Without crops the image shows whole.
const MARK = '<span class="tg-figure-mark" aria-hidden="true"></span>'
const CONNECTOR = '<span class="tg-connector" aria-hidden="true"></span>'

function mediaFigure({ alt, figure, crops, splice }, url) {
  if (!crops) return `<figure class="tg-figure"><img class="tg-media" src="${url}" alt="${alt}"></figure>`

  const parts = crops.map(({ name, mark }) =>
    `<span class="tg-crop tg-crop--${name}"><img class="tg-media" src="${url}" alt="">${mark ? MARK : ""}</span>`)
  const body = splice ? `<span class="tg-splice">${parts.join("")}</span>` : parts.join(CONNECTOR)
  return `<figure class="tg-figure tg-figure--${figure}" role="img" aria-label="${alt}">${body}</figure>`
}

// A step with a selector whose element is gone falls back to the page heading, so
// the seller still sees the explanation after an unrelated markup change.
export function resolveTarget(selector, doc = document) {
  if (!selector) return undefined
  const element = doc.querySelector(selector)
  if (element) return element

  console.warn(`[tour] missing target ${selector}, highlighting the page heading instead`)
  return doc.querySelector("h1") ?? undefined
}
